import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDeals } from '../hooks/useDeals';
import { useContacts } from '../hooks/useContacts';
import { useCompanies } from '../hooks/useCompanies';
import { useToast } from '../hooks/useToast';
import { useSettings } from '../hooks/useSettings';
import DealModal from '../components/deals/DealModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { ToastContainer } from '../components/common/Toast';
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import confetti from 'canvas-confetti';
import { formatCurrency, getInitials, daysSinceCreation, getAgeColor } from '../utils/formatters';

const Pipeline = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { deals, loading: dealsLoading, addDeal, updateDeal } = useDeals();
  const { contacts } = useContacts();
  const { companies } = useCompanies();
  const { settings, loading: settingsLoading } = useSettings();
  const toast = useToast();

  const DEAL_STAGES = useMemo(() => {
    if (settings?.pipelines?.length > 0) {
      const defaultPipeline = settings.pipelines.find(p => p.isDefault) || settings.pipelines[0];
      if (defaultPipeline.stages?.length > 0) {
        return defaultPipeline.stages.map(s => s.name);
      }
    }
    return ['Contacts', 'Proposal', 'Negotiation', 'Won', 'Lost'];
  }, [settings]);

  const loading = dealsLoading || settingsLoading;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Handle navigation state to open new deal modal
  useEffect(() => {
    if (location.state?.openNewDeal) {
      setIsModalOpen(true);
      // Clear the state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const dealsByStage = useMemo(() => {
    const filtered = deals.filter(deal =>
      deal.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return DEAL_STAGES.reduce((acc, stage) => {
      acc[stage] = filtered.filter(deal => deal.stage === stage);
      return acc;
    }, {});
  }, [deals, searchQuery, DEAL_STAGES]);

  const getPrimaryContactName = (deal) => {
    // First try _displayCache for fast access (like extension does)
    if (deal._displayCache?.primaryContactName) {
      return deal._displayCache.primaryContactName;
    }
    // Fall back to contacts lookup
    if (deal.contactIds && deal.contactIds.length > 0) {
      const primaryContact = contacts.find(c => c.id === deal.contactIds[0]);
      if (primaryContact) {
        return primaryContact.name;
      }
    }
    return 'No contact';
  };

  const getCompanyData = (deal) => {
    // First try _displayCache for fast access
    if (deal._displayCache?.companyName) {
      return {
        name: deal._displayCache.companyName,
        logoUrl: deal._displayCache.companyLogoUrl
      };
    }
    // Fall back to companies lookup
    if (deal.companyId) {
      const company = companies.find(c => c.id === deal.companyId);
      if (company) {
        return {
          name: company.name,
          logoUrl: company.logoUrl
        };
      }
    }
    return { name: 'Unknown Company', logoUrl: null };
  };

  const handleAddDeal = () => {
    setEditingDeal(null);
    setIsModalOpen(true);
  };

  const handleEditDeal = (deal) => {
    setEditingDeal(deal);
    setIsModalOpen(true);
  };

  const handleSubmitDeal = async (formData) => {
    setModalLoading(true);
    try {
      if (editingDeal) {
        await updateDeal(editingDeal.id, formData);
        toast.success('Deal updated successfully');
      } else {
        await addDeal(formData);
        toast.success('Deal created successfully');
      }
      setIsModalOpen(false);
      setEditingDeal(null);
    } catch (error) {
      toast.error(error.message || 'Failed to save deal');
    } finally {
      setModalLoading(false);
    }
  };

  const handleStageChange = async (dealId, newStage, newStagePosition) => {
    try {
      const updateData = { stage: newStage };

      if (newStagePosition !== undefined) {
        updateData.stagePosition = newStagePosition;
      }

      await updateDeal(dealId, updateData);
      toast.success('Deal stage updated');

      if (newStage === 'Won') {
        triggerConfetti();
      }
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const triggerConfetti = () => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 9999,
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const dealId = active.id;
    const deal = deals.find(d => d.id === dealId);
    if (!deal) return;

    const isOverStage = DEAL_STAGES.includes(over.id);

    if (isOverStage) {
      const newStage = over.id;
      if (deal.stage !== newStage) {
        let newStagePosition = deal.stagePosition;
        if (settings?.pipelines?.length > 0) {
          const defaultPipeline = settings.pipelines.find(p => p.isDefault) || settings.pipelines[0];
          const stageObj = defaultPipeline.stages?.find(s => s.name === newStage);
          if (stageObj) {
            newStagePosition = stageObj.position;
          }
        }
        handleStageChange(dealId, newStage, newStagePosition);
      }
    } else {
      const targetDeal = deals.find(d => d.id === over.id);
      if (targetDeal && targetDeal.stage !== deal.stage) {
        let newStagePosition = deal.stagePosition;
        if (settings?.pipelines?.length > 0) {
          const defaultPipeline = settings.pipelines.find(p => p.isDefault) || settings.pipelines[0];
          const stageObj = defaultPipeline.stages?.find(s => s.name === targetDeal.stage);
          if (stageObj) {
            newStagePosition = stageObj.position;
          }
        }
        handleStageChange(dealId, targetDeal.stage, newStagePosition);
      }
    }
  };

  const getStageColor = (stageName) => {
    if (settings?.pipelines?.length > 0) {
      const defaultPipeline = settings.pipelines.find(p => p.isDefault) || settings.pipelines[0];
      const stage = defaultPipeline.stages?.find(s => s.name === stageName);
      if (stage?.color) {
        return stage.color;
      }
    }
    const colors = {
      'Contacts': '#94a3b8',
      'Proposal': '#5a8f7b',
      'Negotiation': '#f59e0b',
      'Won': '#4caf50',
      'Lost': '#ef4444'
    };
    return colors[stageName] || '#94a3b8';
  };

  // Droppable Column Component
  const DroppableColumn = ({ stage, children }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: stage,
    });

    return (
      <div
        ref={setNodeRef}
        className={`flex flex-col gap-3 h-full overflow-y-auto pb-4 scrollbar-thin transition-colors rounded-lg ${
          isOver ? 'bg-primary-50 ring-2 ring-primary-200' : ''
        }`}
      >
        {children}
      </div>
    );
  };

  // Draggable Deal Card Component - Aligned with Extension design
  const DealCard = ({ deal }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: deal.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const companyData = getCompanyData(deal);
    const primaryContact = getPrimaryContactName(deal);
    const daysOld = daysSinceCreation(deal.createdAt);
    const ageColorClass = getAgeColor(daysOld);

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={() => navigate(`/deal-details?id=${deal.id}`)}
        className="bg-white rounded-xl p-4 border border-neutral-200 hover:border-primary-200 cursor-pointer group shadow-sm hover:shadow-md transition-all"
      >
        {/* Header: Company Logo/Initials + Company Name + Deal Title */}
        <div className="flex items-start gap-3 mb-3">
          {companyData.logoUrl ? (
            <img
              src={companyData.logoUrl}
              alt={companyData.name}
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-semibold text-sm">
                {getInitials(companyData.name)}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-neutral-500 truncate">{companyData.name}</p>
            <h4 className="text-neutral-900 font-semibold group-hover:text-primary transition-colors truncate">
              {deal.title}
            </h4>
          </div>
          {/* Edit button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditDeal(deal);
            }}
            className="text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity hover:text-primary p-1 flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[18px]">edit</span>
          </button>
        </div>

        {/* Meta: Primary Contact + Value */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-neutral-500 flex items-center gap-1">
            <span className="material-symbols-outlined text-[14px]">person</span>
            <span className="truncate max-w-[120px]">{primaryContact}</span>
          </span>
          {deal.value > 0 && (
            <span className="text-sm font-bold text-primary">
              {formatCurrency(deal.value)}
            </span>
          )}
        </div>

        {/* Footer: Days since creation */}
        <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
          <span className={`text-xs flex items-center gap-1 ${ageColorClass}`}>
            <span className="material-symbols-outlined text-[14px]">schedule</span>
            {daysOld === 0 ? 'Today' : `${daysOld}d ago`}
          </span>
          {deal.category && (
            <span className="bg-secondary-100 text-secondary-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide">
              {deal.category}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" message="Loading pipeline..." />
      </div>
    );
  }

  return (
    <main className="flex-1 flex flex-col relative bg-page">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />

      <DealModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingDeal(null);
        }}
        deal={editingDeal}
        onSubmit={handleSubmitDeal}
        loading={modalLoading}
      />

      {/* Header */}
      <header className="sticky -top-4 md:-top-8 border-b border-neutral-200 bg-white backdrop-blur-sm z-[200]">
        <div className="px-6 py-5 flex flex-col gap-5">
          <div className="flex flex-wrap justify-between items-end gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="font-display text-3xl text-neutral-900 tracking-tight">Sales Pipeline</h2>
              <p className="text-neutral-500 text-sm">
                Manage your opportunities and ongoing deals ({deals.length} total)
              </p>
            </div>
            <button
              onClick={handleAddDeal}
              className="flex items-center justify-center gap-2 bg-primary hover:bg-primary-500 text-white px-5 py-2.5 rounded-lg shadow-md shadow-primary/25 transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              <span className="material-symbols-outlined text-[20px]">add</span>
              <span className="text-sm font-semibold">New Opportunity</span>
            </button>
          </div>
          {/* Search */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative w-full max-w-sm group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="material-symbols-outlined text-neutral-400 group-focus-within:text-primary transition-colors text-[20px]">
                  search
                </span>
              </div>
              <input
                className="block w-full pl-10 pr-3 py-2.5 border border-neutral-200 rounded-lg bg-white text-neutral-900 placeholder-neutral-400 focus:ring-2 focus:ring-primary-200 focus:border-primary transition-all text-sm outline-none"
                placeholder="Search deals..."
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Kanban Board */}
      {deals.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon="handshake"
            title="No deals yet"
            description="Create your first opportunity to start tracking sales"
            actionLabel="New Opportunity"
            onAction={handleAddDeal}
          />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragEnd={handleDragEnd}
        >
          <div className="flex-1 overflow-x-auto p-6 bg-page">
            <div className="flex h-full gap-5 min-w-[1100px]">
              {DEAL_STAGES.map(stage => (
                <div key={stage} id={stage} className="flex flex-col w-[300px] shrink-0">
                  {/* Column Header */}
                  <div className="flex items-center justify-between mb-4 px-1">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getStageColor(stage) }}
                      />
                      <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wider">
                        {stage}
                      </h3>
                      <span className="ml-1 px-2 py-0.5 rounded-full bg-neutral-100 text-xs text-neutral-600 font-medium">
                        {dealsByStage[stage].length}
                      </span>
                    </div>
                  </div>

                  {/* Cards */}
                  <SortableContext
                    items={dealsByStage[stage].map(d => d.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <DroppableColumn stage={stage}>
                      {dealsByStage[stage].length === 0 ? (
                        <div className="text-center py-8 text-neutral-400 text-sm border-2 border-dashed border-neutral-200 rounded-lg">
                          No deals in this stage
                        </div>
                      ) : (
                        dealsByStage[stage].map(deal => (
                          <DealCard key={deal.id} deal={deal} />
                        ))
                      )}
                    </DroppableColumn>
                  </SortableContext>
                </div>
              ))}
            </div>
          </div>
        </DndContext>
      )}
    </main>
  );
};

export default Pipeline;
